import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useListLearningModulesQuery, useListModuleCardsQuery } from 'src/redux/slices/api/su';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import { LearningModuleQueryType, LearningModuleApiData, ModuleCardApiData } from 'src/types/api/learningModule';
import { InstanceLanguage } from 'src/constants/languageSelector';
import { InteractiveContentCard, SuCarousel } from '@amzn/seller-university-ui-components';
import { KatSpinner } from '@amzn/katal-react';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { BUNDLE_NAME, LEARNING_PATHWAY_STRING_ID } from 'src/constants/localization';
import { timeConverter } from 'src/utils/timeFormat';
import ErrorView from 'src/components/ErrorView';
import { ROUTE_SEARCH_PARAM } from 'src/constants/route';
import { FeatureName } from 'src/metrics/metricsFields';
import useUserMetrics from 'src/metrics/useUserMetrics';
import { useAppSelector } from 'src/redux/hooks';
import { selectIsWeblabInT1 } from 'src/redux/slices/weblabs';
import { WeblabId } from 'src/constants/weblab';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';

export const LEARNING_MODULES_CAROUSEL_TEST_IDS = {
  LOADING_CONTAINER: 'learning-modules-carousel-loading',
} as const;

interface LearningModulesCarouselProps {
  subtopicId?: string;
  tagIds?: string[];
  className?: string;
  onEmpty?: () => void; // Callback when no modules are available
}

const LearningModulesCarousel: React.FC<LearningModulesCarouselProps> = ({
  subtopicId,
  tagIds,
  className,
  onEmpty,
}) => {
  const [learningPathwayBundle] = useBundle(BUNDLE_NAME.LEARNING_PATHWAY);
  const { clickMetric } = useUserMetrics();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const isPolyMigration = useAppSelector((state) => selectIsWeblabInT1(state, WeblabId.NalandaPolyMigration));

  const hasValidInput = !!(subtopicId || tagIds?.length);

  const queryPattern = React.useMemo(() => {
    const conditions = [
      subtopicId && {
        queryType: LearningModuleQueryType.SubTopic,
        subtopicQuery: { subtopicId },
      },
      tagIds && {
        queryType: LearningModuleQueryType.Tags,
        tagsQuery: { tagIds },
      },
    ];

    return conditions.find(Boolean) || null;
  }, [subtopicId, tagIds]);

  // T1 path: use ListModuleCards (Poly)
  const {
    data: moduleCardsData,
    isLoading: isModuleCardsLoading,
    isError: isModuleCardsError,
  } = useListModuleCardsQuery({
    clientId,
    selLocale,
    queryPattern: queryPattern!,
  }, { skip: !isPolyMigration || !hasValidInput || !queryPattern });

  // C path: use ListModules (NCPS)
  const {
    data: modulesData,
    isLoading: isModulesLoading,
    isError: isModulesError,
  } = useListLearningModulesQuery({
    clientId,
    selLocale,
    queryPattern: queryPattern!,
  }, { skip: isPolyMigration || !hasValidInput || !queryPattern });

  // Unified state
  const isLoading = isPolyMigration ? isModuleCardsLoading : isModulesLoading;
  const isError = isPolyMigration ? isModuleCardsError : isModulesError;
  const modules: (LearningModuleApiData | ModuleCardApiData)[] = isPolyMigration
    ? (moduleCardsData?.cards || [])
    : (modulesData?.modules || []);

  // Detect empty results (data loaded successfully but no items)
  const isEmpty = !isLoading && !isError && hasValidInput && !modules.length;
  const tagIdsKey = tagIds?.join(',');

  // Log empty results as a side effect (once per empty response, not per render)
  useEffect(() => {
    if (!isEmpty || !isPolyMigration) return;
    if (queryPattern?.queryType === LearningModuleQueryType.SubTopic) {
      // SUB_TOPIC should never be empty — indicates a data issue
      ClientLogger.error(ClientLog.IllegalInternalDataState, {
        message: 'ListModuleCards returned empty cards for subtopic',
        subtopicId,
        url: window.location.href,
      });
    } else {
      // TAGS can legitimately be empty (business logic)
      ClientLogger.warn(ClientLog.PayloadProcessingError, {
        message: 'ListModuleCards returned empty cards list',
        queryType: queryPattern?.queryType,
        tagIds,
        url: window.location.href,
      });
    }
  }, [isEmpty, isPolyMigration, queryPattern?.queryType, subtopicId, tagIdsKey]);

  if (!hasValidInput) {
    return null;
  }

  const getDefaultInstance = (learningModule: LearningModuleApiData | ModuleCardApiData) => {
    return learningModule.instanceMap?.[learningModule.defaultLanguage as InstanceLanguage];
  };

  const getContentVariant = (learningModule: LearningModuleApiData | ModuleCardApiData) => {
    const defaultInstance = getDefaultInstance(learningModule);
    const instanceType = defaultInstance?.instanceType;
    if (instanceType?.toUpperCase() === 'VIDEO') {
      return 'Video' as const;
    }
    if (instanceType?.toUpperCase() === 'DOCUMENT') {
      return 'Pdf' as const;
    }
    return undefined;
  };

  const getModuleMetadata = (learningModule: LearningModuleApiData | ModuleCardApiData): string[] => {
    const defaultInstance = getDefaultInstance(learningModule);

    if (!defaultInstance) return [];

    if (defaultInstance.instanceType?.toUpperCase() === 'VIDEO' && defaultInstance.duration) {
      // For video, convert duration from seconds to minutes:seconds format
      return [timeConverter(defaultInstance.duration)];
    }
    if (defaultInstance.instanceType?.toUpperCase() === 'DOCUMENT' && defaultInstance.pageSize) {
      // For PDF, show page count
      return [t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_PDF_PAGES,
        {
          count: defaultInstance.pageSize,
        },
      )];
    }
    return [];
  };

  if (isLoading) {
    return (
      <div
        className="flex min-h-[15rem] items-center justify-center"
        data-testid={LEARNING_MODULES_CAROUSEL_TEST_IDS.LOADING_CONTAINER}
      >
        <KatSpinner />
      </div>
    );
  }

  // Handle errors
  if (isError) {
    return <ErrorView />;
  }

  // Handle empty results
  if (isEmpty) {
    if (isPolyMigration) {
      // T1: hide carousel for both SUB_TOPIC and TAGS
      onEmpty?.();
      return null;
    }
    // C: existing behavior
    if (queryPattern?.queryType === LearningModuleQueryType.Tags) {
      onEmpty?.();
      // Spike: Homepage Seller-to-Seller Module Carousel Implementation | Quip Doc
      // https://quip-amazon.com/NM3NAoQ5Pwb0/Spike-Homepage-Seller-to-Seller-Module-Carousel-Implementation
      return null; // Hide carousel completely for empty TAGS results
    }
    return <ErrorView />; // Show error view for SUB_TOPIC empty results
  }

  return (
    <SuCarousel
      items={modules}
      getItemKey={(module: LearningModuleApiData | ModuleCardApiData) => module.moduleId}
      renderItem={(learningModule: LearningModuleApiData | ModuleCardApiData) => {
        const contentVariant = getContentVariant(learningModule);
        return (
          <InteractiveContentCard
            className="h-full"
            title={learningModule.title}
            previewDescription={learningModule.description}
            imageSrc={learningModule.thumbnailLink}
            {...(contentVariant && { contentVariant })}
            metadata={getModuleMetadata(learningModule)}
            layout="vertical"
            onClick={() => {
              clickMetric({
                feature: FeatureName.LearningModuleCard,
                metadata: {
                  url: window.location.href,
                  moduleId: learningModule.moduleId,
                  moduleTitle: learningModule.title,
                  parentId: subtopicId || `tags=${tagIds?.join(',')}`,
                },
              });

              const updatedSearchParams = new URLSearchParams(searchParams);
              updatedSearchParams.set(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID, learningModule.moduleId);
              navigate({ search: updatedSearchParams.toString() }, { replace: true });
            }}
          />
        );
      }}
      layout={{
        xsmall: { cols: 1 },
        small: { cols: 2 },
        medium: { cols: 3 },
        large: { cols: 4 },
      }}
      className={className || '-mx-su-space-10'}
    />
  );
};

export default LearningModulesCarousel;
